/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Holds the anti-bot clearance a solved challenge earned.
 *
 * The score check runs on every mutating route, and on a low-traffic key genuine
 * people land in the CHALLENGE band -- so one onboarding asked for a puzzle at
 * agency creation, again at plan, again at payment, each solved token single-use
 * by design. The backend now answers a redemption with a short-lived clearance;
 * echoing it back means the next CHALLENGE accepts it instead of asking the same
 * person the same question again.
 *
 * Backed by sessionStorage, never localStorage or a cookie: it is per-tab and
 * gone when the tab closes, so it does not outlive the session or survive a
 * shared machine. Memory-only used to be the choice here, but core flows
 * (property edit, OAuth round-trips, checkout) do a full document reload,
 * which wiped memory and re-asked the same person the same question. The
 * clearance is already a short-lived (90 min), bearer-bound, HMAC-signed
 * credential -- sessionStorage does not widen what an XSS on this page could
 * already do by reading it out of memory while the tab is open.
 */

export const CLEARANCE_HEADER = 'X-Anti-Bot-Clearance';

const STORAGE_KEY = 'housien.antibot.clearance';

/**
 * Reads the persisted anti-bot clearance token from session storage.
 *
 * @returns The persisted clearance token, or null if absent/unreachable.
 */
function readPersisted(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

let clearance: string | null = readPersisted();

/**
 * Remembers the clearance returned by a redeemed request.
 *
 * @param value The header value, or null/undefined on responses that carry none
 *              (which is most of them -- only a redemption mints one).
 */
export function rememberClearance(value: string | null | undefined): void {
  if (!value) return;
  clearance = value;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Private browsing or a full storage quota: memory still holds it for this tab.
  }
}

/**
 * The current clearance, or null when the user has not solved a challenge yet.
 *
 * Not consumed on read: it covers the window, not one request -- that is the
 * whole point. The backend decides when it has expired; the client holding a
 * stale one costs a single rejected header and the challenge the user would
 * have been asked for anyway.
 */
export function currentClearance(): string | null {
  return clearance;
}

/**
 * Drops the clearance -- on sign-out, so it never follows a different user.
 *
 * @returns Nothing.
 */
export function forgetClearance(): void {
  clearance = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up if storage was never reachable.
  }
}
